import { Link } from "react-router-dom";

import { Logo } from "@/components/lembra/Logo";
import { PageMeta } from "@/components/lembra/PageMeta";
import { SkipToContent } from "@/components/lembra/SkipToContent";

/**
 * Privacy Policy. Aligned with the Australian Privacy Principles (APPs).
 *
 * Copy rule: no em-dashes in body text. Commas, full stops, parens,
 * or line breaks.
 */
const Privacy = () => (
  <>
    <PageMeta
      title="Privacy Policy | Lembra"
      description="How Lembra collects, uses, and protects your information. Aligned with the Australian Privacy Principles."
      path="/privacy"
    />
    <SkipToContent />
  <div className="min-h-screen bg-cream flex flex-col">
    <header className="py-6 px-6 md:px-10">
      <Logo size="md" />
    </header>
    <main id="main" className="flex-1 max-w-3xl mx-auto px-6 md:px-10 pb-16 w-full">
      <h1 className="text-3xl md:text-4xl font-bold text-navy mb-4">Privacy Policy</h1>
      <p className="text-charcoal/60 text-sm mb-6">Last updated: 20 April 2026</p>


      <article className="prose prose-charcoal max-w-none space-y-6 text-charcoal leading-relaxed">
        <section>
          <h2 className="text-xl font-bold text-navy">1. About this policy</h2>
          <p>
            Lembra Pty Ltd is committed to protecting your privacy and complying with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs). This policy explains what personal information we collect, how we use and share it, and the rights you have in relation to it.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">2. Who we are</h2>
          <p>
            Lembra Pty Ltd<br />
            Registered office: 11 Wilson Street, South Yarra VIC 3141<br />
            Privacy contact: <a href="mailto:support@lembra.com.au" className="text-coral underline">support@lembra.com.au</a>
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">3. What we collect</h2>
          <p>We collect only the information we need to run the service:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account information:</strong> full name, work email address, password (stored as a hash), and company name.</li>
            <li><strong>Team member details:</strong> names, birthdays, work start dates, dietary requirements, department, and (optional) email addresses for team members you add.</li>
            <li><strong>Celebration and order information:</strong> celebration type (birthday, anniversary, welcome, farewell), delivery address, date and time window, gift preferences (cake, hamper, flowers, voucher, workshop, spa, etc.), special instructions, and order history.</li>
            <li><strong>Payment information:</strong> card brand, last four digits, and expiry. Full card numbers are handled directly by our payment processor (Stripe) and never stored on Lembra's systems.</li>
            <li><strong>Usage information:</strong> log data, IP address, browser type, device type, and pages visited, collected via standard web analytics and error monitoring (Sentry).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">4. How we use your information</h2>
          <p>We use personal information to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Provide the celebration platform, including scheduling reminders, processing orders, and coordinating with our vendor partners.</li>
            <li>Process payments and issue invoices.</li>
            <li>Communicate with you about your account, orders, service changes, and support queries.</li>
            <li>Improve the platform's reliability and performance (error tracking, aggregated usage analytics).</li>
            <li>Comply with our legal obligations, including record-keeping and tax compliance under Australian law.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">5. Sensitive information</h2>
          <p>
            Dietary requirements can indicate religious or health information, which is treated as sensitive information under the Privacy Act. We collect dietary details only with the intent of ensuring recipients receive food that is safe and appropriate for them. For food-based orders (cakes, hampers, and similar), we share only the specific dietary details the vendor needs to produce the order. We don't pass dietary information to non-food vendors (florists, spa, workshops, vouchers) since it's not relevant to their fulfilment.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">6. Who we share your information with</h2>
          <p>We share personal information only in the following circumstances:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Vendor partners:</strong> when you place an order, the relevant vendor (bakery, hamper maker, florist, spa, workshop, voucher provider, etc.) receives the recipient's name, the dietary requirements relevant to that category, delivery address, delivery date/time, and any special instructions. That is the minimum needed to fulfil and deliver the order.</li>
            <li><strong>Service providers:</strong> we use a small number of trusted third parties to operate the platform:
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Supabase (database and authentication; data hosted in Southeast Asia, Singapore region).</li>
                <li>Stripe (payment processing; data handled under Stripe's own privacy policy).</li>
                <li>Resend (transactional email delivery).</li>
                <li>Sentry (error monitoring; EU region).</li>
                <li>Lovable (hosting of the web application).</li>
              </ul>
            </li>
            <li><strong>Legal and safety:</strong> where we are required by law, by a court order, or where we reasonably believe it is necessary to protect our rights, safety, or the rights and safety of others.</li>
          </ul>
          <p>
            We do not sell your personal information to anyone, and we do not use it to train advertising models.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">7. Cross-border data transfers</h2>
          <p>
            Some of our service providers store or process data outside Australia (e.g., Supabase's Singapore region; Sentry's EU region). By using the platform you consent to this transfer. We take reasonable steps to ensure each provider handles your data with a level of protection comparable to that required under Australian law.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">8. How long we keep your information</h2>
          <p>
            We retain personal information only for as long as we need it for the purposes described in this policy or as required by law. Order and payment records are kept for a minimum of seven years to comply with Australian tax and accounting rules. When you close your account, we delete or de-identify personal data except where retention is required by law.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">9. How we protect your information</h2>
          <p>
            We use industry-standard security measures including TLS encryption in transit, encryption at rest on our database, row-level security policies to isolate each company's data, restricted access to production systems, and logged audit trails for administrative actions. No system is perfectly secure; if we become aware of a data breach that is likely to cause serious harm, we will notify you and the Office of the Australian Information Commissioner (OAIC) as required by the Notifiable Data Breaches scheme.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">10. Your rights</h2>
          <p>You have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Access the personal information we hold about you.</li>
            <li>Request correction of information that is inaccurate, incomplete, or out of date.</li>
            <li>Request deletion of your personal information, subject to the retention obligations in section 8.</li>
            <li>Withdraw consent where we rely on consent to process your information.</li>
            <li>Make a privacy complaint.</li>
          </ul>
          <p>
            To exercise any of these rights, email <a href="mailto:support@lembra.com.au" className="text-coral underline">support@lembra.com.au</a>. We will respond within 30 days.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">11. Cookies and similar technologies</h2>
          <p>
            We use a small number of cookies to keep you signed in and to understand how the platform is used. You can block cookies in your browser settings, but some features (including login) may stop working.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">12. Complaints</h2>
          <p>
            If you believe we have mishandled your personal information, please contact us at <a href="mailto:support@lembra.com.au" className="text-coral underline">support@lembra.com.au</a>. If you are not satisfied with our response, you may lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="text-coral underline">oaic.gov.au</a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">13. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. Material changes will be communicated to you by email or through a notice on the platform before they take effect.
          </p>
        </section>
      </article>

      <div className="mt-10">
        <Link to="/" className="text-sm text-coral hover:underline">← Back to home</Link>
      </div>
    </main>
  </div>
  </>
);

export default Privacy;
